import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, patientAPI } from '../utils/api/directus';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPatientForUser = async (userData, source = 'unknown') => {
    if (!userData) return null;

    try {
      if (userData.patient_id) {
        const patientData = await patientAPI.getPatientInfo(userData.patient_id);
        setPatient(patientData);
        console.log(`[Auth] ${source}: Loaded patient by patient_id:`, patientData);
        return patientData;
      }

      console.warn(`[Auth] ${source}: user has no patient_id. Trying to look up by user_id relation.`);
      const patientData = await patientAPI.getPatientByUserId(userData.id);

      if (patientData) {
        setPatient(patientData);
        console.log(`[Auth] ${source}: Found patient via user relation:`, patientData);

        const updatedUser = { ...userData, patient_id: patientData.id };
        setUser(updatedUser);
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        console.log('[Auth] Stored updated user with patient_id in localStorage.');
      } else {
        console.warn('[Auth] No patient found for this user.');
      }

      return patientData;
    } catch (error) {
      console.error('[Auth] Failed to load patient for user:', error);
      return null;
    }
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');

      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          console.log('[Auth] Loaded user from storage:', parsedUser);

          await loadPatientForUser(parsedUser, 'init');
        } catch (err) {
          console.error('Auth initialization error:', err);
          // Clear invalid data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  /**
   * Login function
   */
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // Login and get tokens
      await authAPI.login(username, password);

      // Get current user data
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      console.log('[Auth] Directus user data:', userData);

      // Get patient data if available
      await loadPatientForUser(userData, 'login');

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setPatient(null);
      setError(null);
    }
  };

  /**
   * Refresh user data
   */
  const refreshUserData = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      console.log('[Auth] Refreshed user data:', userData);

      await loadPatientForUser(userData, 'refresh');
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  const value = {
    user,
    patient,
    loading,
    error,
    login,
    logout,
    refreshUserData,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

